// Per-mission MEMORY, SQUAD and STORIES — the data behind the Memory tab, the
// Squad tab, and the Stories surface. Keyed by door_type (one coherent scenario
// per door, matching _mock/discussions.js) with per-mission overrides where a
// concrete mission needs its own content (e.g. the several `goal` missions).
//
// The DATA lives in mission-data.js (auto-generated from _mock/_gen/*.json);
// this module owns the graph builder, the fallbacks and the accessors.
// Mock-only and deterministic so every screen renders rich without a backend.
// Memory shape mirrors the champion memory (see components/memory/MemoryPanel).

import {
  memoryByDoor, squadByDoor, storiesByDoor,
  memoryByMission, squadByMission, storiesByMission,
} from './mission-data.js';
// Life Connect (adb) demo missions carry their OWN squad (generated by
// .claude/seed-life-connect.mjs) — wins over the door fallback.
import { SQUAD_BY_MISSION_LC } from './lc-overview.generated.js';
import { isDemoMission } from './mock-scope.js';

// ── Graph builder ─────────────────────────────────────────────────────────────
// Turns short label lists (a memory entry's `graphSpec`) into a hub-and-spoke
// force graph (+ a few cross-links for texture).
export function buildGraph(hub, spec = {}) {
  const nodes = [{ id: 'hub', type: 'entity', label: hub, isHub: true }];
  const edges = [];
  const add = (type, labels = [], prefix) =>
    labels.map((label, i) => {
      const id = `${prefix}-${i}`;
      nodes.push({ id, type, label });
      edges.push({ source: 'hub', target: id });
      return id;
    });
  const ent = add('entity', spec.entities, 'e');
  const ins = add('insight', spec.insights, 'ins');
  const src = add('source', spec.sources, 'src');
  const jud = add('judgment', spec.judgments, 'j');
  const reg = add('regulation', spec.regulations, 'reg');
  const bs = add('backstory', spec.backstory, 'bs');
  src.forEach((s, i) => { if (jud[i]) edges.push({ source: s, target: jud[i] }); else if (ent[i]) edges.push({ source: s, target: ent[i] }); });
  ins.forEach((s, i) => { if (bs[i]) edges.push({ source: s, target: bs[i] }); });
  ent.forEach((s, i) => { if (reg[i]) edges.push({ source: s, target: reg[i] }); });
  return { nodes, edges };
}

// ── Fallbacks (any door/mission without authored content still renders) ───────
export const FALLBACK_MEMORY = {
  graphSpec: {
    hub: 'Mission',
    entities: ['Objectif', 'Contraintes', 'Périmètre'],
    insights: ['Hypothèses posées', 'Agents assignés'],
    sources: ['Brief'],
    judgments: [],
    regulations: ['Rester dans le périmètre'],
    backstory: ['Contexte capturé'],
  },
  backstory:
    "La mémoire de cette mission se construit au fil de l'eau : à mesure que les agents avancent, concepts, sources et jugements remontent ici et alimentent le graphe de connaissance.",
  regulations: [
    { id: 'r1', text: 'Rester dans le périmètre du Brief ; toute extension est une mission de suivi.', severity: 'soft' },
  ],
  judgments: [],
  proposals: [],
};

export const FALLBACK_SQUAD = {
  lead: { name: 'Nova', champion_slug: 'nova-am', role: 'Account Manager', mentalModel: 'Cadrer puis déléguer' },
  permanent: [
    { name: 'André', champion_slug: 'simon-strategist', role: 'Architect', mentalModel: 'Voir la structure' },
  ],
  temporary: [],
  stakeholders: [
    { name: 'Greg', role: 'Product Owner', human_role: 'stakeholder' },
  ],
};

export const FALLBACK_STORIES = [];

// ── Empty shapes for NON-demo offices ─────────────────────────────────────────
// Outside Life Connect there is no authored content — return empty rather than
// borrowing the demo's fixtures, so the panels render honest empty states.
export const EMPTY_SQUAD = { lead: null, permanent: [], temporary: [], stakeholders: [] };
export const EMPTY_MEMORY = {
  graph: { nodes: [], edges: [] },
  backstory: '',
  regulations: [],
  judgments: [],
  proposals: [],
};

// ── Accessors (per-mission override → door default → fallback) ────────────────
// Memory entries carry a pure-JSON `graphSpec` (hub + label arrays) that we
// build on access, or a pre-built `graph`.
function resolveMemory(m) {
  if (!m) return resolveMemory(FALLBACK_MEMORY);
  if (m.graph || !m.graphSpec) return m;
  return { ...m, graph: buildGraph(m.graphSpec.hub, m.graphSpec) };
}

export function memoryFor(mission) {
  if (!isDemoMission(mission)) return EMPTY_MEMORY;
  return resolveMemory(memoryByMission[mission?.id] ?? memoryByDoor[mission?.door_type] ?? FALLBACK_MEMORY);
}
export function squadFor(mission) {
  if (!isDemoMission(mission)) return EMPTY_SQUAD;
  return SQUAD_BY_MISSION_LC[mission?.id] ?? squadByMission[mission?.id] ?? squadByDoor[mission?.door_type] ?? FALLBACK_SQUAD;
}
export function storiesFor(mission) {
  if (!isDemoMission(mission)) return FALLBACK_STORIES;
  return storiesByMission[mission?.id] ?? storiesByDoor[mission?.door_type] ?? FALLBACK_STORIES;
}

// ── Mission filiation (parent / engendered children) ──────────────────────────
// Mock the door-to-door narrative : a reasoning mission concludes and engenders
// downstream work. { parent?: {title, door, id?}, children?: [{title, door, id?}] }
// — an `id` makes the pill a link to a real demo mission.
const RELATIONS_BY_MISSION = {
  'm-d1': { children: [{ title: 'Builder le MVP de réservation', door: 'goal' }] },
  'm-d4': { children: [{ title: 'App modulaire unifiée', door: 'goal' }] },
  'm-d2': { children: [{ title: 'Migrer hors d’Odoo ?', door: 'decision' }] },
  'm-d3': { children: [{ title: 'Consolidation mensuelle des frais', door: 'maintain', id: 'm-mnt1' }] },
  'm-p1': { children: [{ title: 'Surveiller le churn', door: 'monitor', id: 'm-d5' }] },
  'm-v2': {
    parent: { title: 'Paiements perdus sur webhooks', door: 'concern' },
    children: [{ title: 'Monitoring des paiements', door: 'maintain', id: 'm-mnt1' }],
  },
  'm-v1': { children: [{ title: 'Surveillance post-migration', door: 'maintain', id: 'm-mnt1' }] },
};
export function relationsFor(mission) {
  if (!isDemoMission(mission)) return {};
  return RELATIONS_BY_MISSION[mission?.id] ?? {};
}
